import React from 'react'
import * as stores from '../stores/appStore'
import PersonCard from './PersonCard'
import AddPersonModal from './AddPersonModal'

const ListView: React.FC = () => {
  const { people, groups, syncAll, isSyncing } = stores.useAppStore()
  const [searchQuery, setSearchQuery] = React.useState('')
  const [selectedGroup, setSelectedGroup] = React.useState<string>('all')
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = React.useState(false)

  // Filter people based on search and group
  const filteredPeople = React.useMemo(() => {
    let filtered = people

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(person =>
        `${person.firstName} ${person.lastName}`.toLowerCase().includes(query) ||
        person.email?.toLowerCase().includes(query)
      )
    }

    // Filter by group
    if (selectedGroup !== 'all') {
      filtered = filtered.filter(person =>
        person.groups.includes(selectedGroup)
      )
    }

    return filtered
  }, [people, searchQuery, selectedGroup])

  const lastSyncTime = React.useMemo(() => {
    const times = people
      .map(p => p.lastSyncedAt)
      .filter(Boolean) as Date[]

    if (times.length === 0) return null

    const mostRecent = new Date(Math.max(...times.map(d => d.getTime())))
    const now = new Date()
    const diffMinutes = Math.floor((now.getTime() - mostRecent.getTime()) / 60000)

    if (diffMinutes < 1) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`
    return `${Math.floor(diffMinutes / 1440)} days ago`
  }, [people])

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All Groups</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.emoji} {group.name}
            </option>
          ))}
        </select>
      </div>

      {/* People Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          People ({filteredPeople.length})
        </h2>
        <button
          onClick={() => setIsAddPersonModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Person
        </button>
      </div>

      {/* People List */}
      <div className="space-y-4">
        {filteredPeople.map(person => (
          <PersonCard key={person.id} person={person} />
        ))}

        {filteredPeople.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchQuery || selectedGroup !== 'all'
              ? 'No people found matching your filters'
              : 'No people added yet'}
          </div>
        )}
      </div>

      {/* Sync Status */}
      {people.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            {lastSyncTime ? `Last synced: ${lastSyncTime}` : 'Never synced'}
          </span>
          <button
            onClick={syncAll}
            disabled={isSyncing}
            className={`inline-flex items-center px-3 py-1 rounded-lg transition-colors ${
              isSyncing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
            }`}
          >
            {isSyncing ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Syncing...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync All
              </>
            )}
          </button>
        </div>
      )}

      {/* Add Person Modal */}
      {isAddPersonModalOpen && (
        <AddPersonModal
          isOpen={isAddPersonModalOpen}
          onClose={() => setIsAddPersonModalOpen(false)}
        />
      )}
    </div>
  )
}

export default ListView